import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { addItem } from '../store/cartSlice.js';
import { fetchProducts } from '../store/productsSlice.js';

const Perfumes = () => {
  const dispatch = useDispatch();
  const products = useSelector((s) => s.products.items || []);
  const user = useSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [gender, setGender] = useState("");
  const [sort, setSort] = useState("");
  const [visibleCards, setVisibleCards] = useState(new Set());
  const observerRef = useRef();

  useEffect(() => {
    dispatch(fetchProducts());
    
    // Intersection Observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards(prev => new Set([...prev, entry.target.dataset.index]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [dispatch]);

  const filtered = useMemo(() => {
    let arr = Array.isArray(products) ? products.slice() : [];
    if (query)
      arr = arr.filter((p) =>
        p.name?.toLowerCase().includes(query.toLowerCase()),
      );
    if (type)
      arr = arr.filter(
        (p) => (p.type || p.quality || "").toLowerCase() === type.toLowerCase(),
      );
    if (gender)
      arr = arr.filter(
        (p) => (p.gender || "").toLowerCase() === gender.toLowerCase(),
      );
    if (sort === "low") arr.sort((a, b) => (a.price || 0) - (b.price || 0));
    if (sort === "high") arr.sort((a, b) => (b.price || 0) - (a.price || 0));
    return arr;
  }, [products, query, type, gender, sort]);

  useEffect(() => {
    // Observe all product cards
    const cards = document.querySelectorAll('.product-card');
    cards.forEach(card => {
      if (observerRef.current) {
        observerRef.current.observe(card);
      }
    });
  }, [filtered]);

  const handleAddToCart = (item, e) => {
    e.stopPropagation();
    if (!user) {
      toast.info("Please sign in to purchase");
      navigate("/signin");
      return;
    }
    dispatch(
      addItem({
        id: item.id,
        name: item.name,
        price: item.price,
        image: item.image,
        description: item.description,
      }),
    );
    toast.success("Added to cart");
  };

  return (
    <div className="section-padding" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
      <div className="container">
        {/* Hero Header */}
        <div className="text-center mb-5 animate-fade-in-up">
          <h1 className="heading-primary mb-3 mb-md-4 px-3">
            Premium Fragrance Collection
          </h1>
          <div className="row justify-content-center">
            <div className="col-lg-8 col-xl-6">
              <p className="lead text-muted mb-0 px-3" style={{ lineHeight: '1.6' }}>
                Discover our curated selection of luxury perfumes, meticulously crafted for the discerning individual who appreciates excellence and sophistication.
              </p>
            </div>
          </div>
        </div>

        {/* Compact Filter Bar */}
        <div className="d-flex flex-wrap gap-2 align-items-center mb-4 animate-fade-in-up animate-delay-1 p-3 rounded-4 bg-white shadow-sm">
          <div className="position-relative flex-grow-1" style={{ minWidth: 160 }}>
            <i className="bi bi-search position-absolute text-muted" style={{ left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13 }} />
            <input
              className="form-control form-control-sm border-0 bg-light ps-4"
              placeholder="Search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ borderRadius: 20 }}
            />
          </div>
          <select
            className="form-select form-select-sm border-0 bg-light"
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{ borderRadius: 20, minWidth: 130, maxWidth: 160 }}
          >
            <option value="">All Categories</option>
            <option value="Premium">Premium</option>
            <option value="Luxury">Luxury</option>
            <option value="Eau de Parfum">Eau de Parfum</option>
          </select>
          <select
            className="form-select form-select-sm border-0 bg-light"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            style={{ borderRadius: 20, minWidth: 130, maxWidth: 160 }}
          >
            <option value="">All Collections</option>
            <option value="Male">Men</option>
            <option value="Female">Women</option>
            <option value="All Genders">Unisex</option>
            <option value="Combo Pack">Gift Sets</option>
          </select>
          <select
            className="form-select form-select-sm border-0 bg-light"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            style={{ borderRadius: 20, minWidth: 130, maxWidth: 160 }}
          >
            <option value="">Featured</option>
            <option value="low">Price: Low → High</option>
            <option value="high">Price: High → Low</option>
          </select>
          <span className="text-muted small ms-auto text-nowrap">
            <i className="bi bi-grid-3x3-gap me-1"></i>{filtered.length} products
          </span>
        </div>

        {/* Professional Product Grid */}
        <div className="row g-4">
          {Array.isArray(filtered) && filtered.map((item, index) => {
            const isVisible = visibleCards.has(index.toString());
            const discountPercent = item.oldPrice
              ? Math.round(((item.oldPrice - item.price) / item.oldPrice) * 100)
              : 0;

            return (
              <div
                key={item.id || index}
                className="col-xl-3 col-lg-4 col-md-6 col-sm-12"
              >
                <div
                  className={`professional-card product-card h-100 ${
                    isVisible ? 'animate-scale-in' : 'scroll-reveal'
                  }`}
                  data-index={index}
                  style={{ borderRadius: '20px', cursor: 'pointer', background: 'white', animationDelay: `${(index % 4) * 0.1}s` }}
                  onClick={() => navigate(`/product/${item.id}`)}
                >
                  {/* Premium Image Container */}
                  <div className="position-relative overflow-hidden" style={{ height: '280px' }}>
                    <img
                      src={(item.images?.[0] || item.image) || "/slider/1.jpeg"}
                      alt={item.name}
                      className="card-img-top w-100 h-100"
                      style={{ objectFit: 'cover' }}
                    />
                    
                    {/* Premium Badges */}
                    <div className="position-absolute top-0 start-0 p-3">
                      {discountPercent > 0 && (
                        <span className="badge bg-danger rounded-pill px-3 py-2 mb-2 d-block w-fit">
                          <i className="bi bi-percent me-1"></i>{discountPercent}% OFF
                        </span>
                      )}
                      <span className="badge bg-dark rounded-pill px-3 py-2">
                        <i className="bi bi-star-fill me-1"></i>Premium
                      </span>
                    </div>
                    
                    {/* Quick Actions Overlay */}
                    <div className="position-absolute bottom-0 start-0 end-0 p-3 bg-gradient" 
                         style={{ 
                           background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                           opacity: 0,
                           transition: 'opacity 0.3s ease'
                         }}
                         onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                         onMouseLeave={(e) => e.currentTarget.style.opacity = 0}>
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-light btn-sm rounded-pill flex-fill"
                          onClick={(e) => { e.stopPropagation(); navigate(`/product/${item.id}`); }}
                        >
                          <i className="bi bi-eye me-1"></i>Quick View
                        </button>
                        <button 
                          className="btn btn-primary btn-sm rounded-pill"
                          onClick={(e) => handleAddToCart(item, e)}
                        >
                          <i className="bi bi-cart-plus-fill"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Professional Card Body */}
                  <div className="card-body p-3 p-md-4 d-flex flex-column">
                    {/* Category & Rating */}
                    <div className="badge-container mb-3">
                      <span className="badge bg-light text-dark rounded-pill px-2 px-md-3 py-1 small">
                        {item.type || 'Premium'}
                      </span>
                      <div className="text-warning small d-flex align-items-center">
                        <div className="me-1">
                          <i className="bi bi-star-fill"></i>
                          <i className="bi bi-star-fill"></i>
                          <i className="bi bi-star-fill"></i>
                          <i className="bi bi-star-fill"></i>
                          <i className="bi bi-star-fill"></i>
                        </div>
                        <span className="text-muted ms-1 small">(4.9)</span>
                      </div>
                    </div>

                    {/* Product Title */}
                    <h5 className="card-title fw-bold text-dark mb-2">
                      {item.name || item.title}
                    </h5>

                    {/* Description */}
                    <p className="card-text text-muted small mb-3 flex-grow-1">
                      {item.description || 'Exquisite fragrance with long-lasting premium quality ingredients crafted for discerning individuals.'}
                    </p>

                    {/* Product Features */}
                    <div className="row g-1 mb-3 small text-muted">
                      <div className="col-6 d-flex align-items-center">
                        <i className="bi bi-droplet-fill text-primary me-1 flex-shrink-0"></i>
                        <span className="text-truncate">{item.volume || '50ml'}</span>
                      </div>
                      <div className="col-6 d-flex align-items-center">
                        <i className="bi bi-clock-fill text-success me-1 flex-shrink-0"></i>
                        <span className="text-truncate">{item.longevity || '8+ Hours'}</span>
                      </div>
                      <div className="col-6 d-flex align-items-center">
                        <i className="bi bi-wind text-info me-1 flex-shrink-0"></i>
                        <span className="text-truncate">{item.sillage || 'Moderate'}</span>
                      </div>
                      <div className="col-6 d-flex align-items-center">
                        <i className="bi bi-thermometer-half text-warning me-1 flex-shrink-0"></i>
                        <span className="text-truncate">{item.season || 'All Season'}</span>
                      </div>
                    </div>

                    {/* Pricing Section */}
                    <div className="border-top pt-3 mb-3">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-baseline gap-2 flex-wrap">
                          {item.oldPrice && (
                            <span className="text-muted text-decoration-line-through small">
                              ₹{item.oldPrice.toLocaleString()}
                            </span>
                          )}
                          <span className="h6 fw-bold text-gradient mb-0">
                            ₹{item.price?.toLocaleString() || '—'}
                          </span>
                        </div>
                        {discountPercent > 0 && (
                          <span className="badge bg-success-subtle text-success small px-2 py-1">
                            Save ₹{(item.oldPrice - item.price).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-grid gap-2 mt-auto">
                      <button
                        className="btn btn-business btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!user) {
                            toast.info("Please sign in to purchase");
                            navigate("/signin");
                            return;
                          }
                          dispatch(
                            addItem({
                              id: item.id,
                              name: item.name,
                              price: item.price,
                              image: item.image,
                              discount: item.discount,
                              oldPrice: item.oldPrice
                            })
                          );
                          navigate("/cart");
                        }}
                      >
                        <i className="bi bi-lightning-fill me-2"></i>Buy Now
                      </button>
                      <button
                        className="btn btn-outline-secondary rounded-pill btn-sm"
                        onClick={(e) => handleAddToCart(item, e)}
                      >
                        <i className="bi bi-cart-plus-fill me-2"></i>Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filtered.length === 0 && (
          <div className="text-center py-5 animate-fade-in-up">
            <div className="mb-4">
              <i className="bi bi-search display-1 text-muted"></i>
            </div>
            <h4 className="text-muted mb-3">No products found</h4>
            <p className="text-muted mb-4">Try adjusting your search criteria or browse our featured collections.</p>
            <button 
              className="btn btn-business"
              onClick={() => {
                setQuery('');
                setType('');
                setGender('');
                setSort('');
              }}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* PRODUCT MODAL - removed, now using dedicated product page */}
    </div>
  );
};

export default Perfumes;
